import { handicapAPI } from '@/services/handicap'

function responseItems (response) {
  const data = response.data || []
  return data.results || data
}

function errorMessage (error) {
  const data = error.response?.data
  if (typeof data === 'string') return data
  if (Array.isArray(data)) return data.join(' ')
  if (data?.detail) return data.detail
  if (data && typeof data === 'object') return Object.values(data).flat().join(' ')
  return error.message || 'No fue posible completar la operación.'
}

const state = {
  players: [],
  allHandicaps: [],
  selectedOverview: null,
  configuration: [],
  myHandicap: null,
  loading: false,
  detailLoading: false,
  operationLoading: false,
  error: null,
  lastFetch: null
}

const getters = {
  myHandicapValue: state => state.myHandicap?.current_handicap_index ?? null,
  sortedHandicaps: state => [...state.players].sort((a, b) => {
    if (a.current_handicap_index === null) return 1
    if (b.current_handicap_index === null) return -1
    return Number(a.current_handicap_index) - Number(b.current_handicap_index)
  }),
  clubAverageHandicap: state => {
    const indexed = state.players.filter(player => player.current_handicap_index !== null)
    if (!indexed.length) return '--'
    const total = indexed.reduce((sum, player) => sum + Number(player.current_handicap_index), 0)
    return (total / indexed.length).toFixed(1)
  },
  bestHandicapInClub: state => state.players
    .filter(player => player.current_handicap_index !== null)
    .reduce((best, player) => {
      if (!best || Number(player.current_handicap_index) < Number(best.current_handicap_index)) return player
      return best
    }, null),
  getPlayerComparison: state => playerId => {
    const indexed = state.players.filter(player => player.current_handicap_index !== null)
    const player = indexed.find(item => item.id === playerId)
    if (!player || !indexed.length) return null
    const sorted = [...indexed].sort((a, b) => Number(a.current_handicap_index) - Number(b.current_handicap_index))
    const position = sorted.findIndex(item => item.id === playerId) + 1
    const average = indexed.reduce((sum, item) => sum + Number(item.current_handicap_index), 0) / indexed.length
    return {
      yourHandicap: Number(player.current_handicap_index).toFixed(1),
      clubAverage: average.toFixed(1),
      bestPlayerHandicap: Number(sorted[0].current_handicap_index).toFixed(1),
      bestPlayerName: sorted[0].display_name,
      position,
      totalPlayers: indexed.length,
      percentile: Math.round(((indexed.length - position) / indexed.length) * 100),
      betterThanYou: position - 1,
      worseThanYou: indexed.length - position,
      differenceVsAverage: (Number(player.current_handicap_index) - average).toFixed(1)
    }
  }
}

const mutations = {
  SET_PLAYERS (state, players) {
    state.players = players
    state.allHandicaps = players
    state.lastFetch = new Date()
  },
  SET_OVERVIEW (state, overview) { state.selectedOverview = overview },
  SET_CONFIGURATION (state, configuration) { state.configuration = configuration },
  SET_MY_HANDICAP (state, handicap) { state.myHandicap = handicap },
  SET_LOADING (state, loading) { state.loading = loading },
  SET_DETAIL_LOADING (state, loading) { state.detailLoading = loading },
  SET_OPERATION_LOADING (state, loading) { state.operationLoading = loading },
  SET_ERROR (state, error) { state.error = error }
}

const actions = {
  async fetchAllHandicaps ({ commit }, params = {}) {
    commit('SET_LOADING', true)
    try {
      const response = await handicapAPI.listPlayers(params)
      const players = responseItems(response)
      commit('SET_PLAYERS', players)
      commit('SET_ERROR', null)
      return players
    } catch (error) {
      commit('SET_ERROR', errorMessage(error))
      return []
    } finally {
      commit('SET_LOADING', false)
    }
  },
  async fetchConfiguration ({ commit }) {
    try {
      const response = await handicapAPI.listConfiguration()
      const configuration = responseItems(response)
      commit('SET_CONFIGURATION', configuration)
      return configuration
    } catch (error) {
      commit('SET_ERROR', errorMessage(error))
      return []
    }
  },
  async fetchPlayerOverview ({ commit }, playerId) {
    commit('SET_DETAIL_LOADING', true)
    try {
      const response = await handicapAPI.getPlayerOverview(playerId)
      commit('SET_OVERVIEW', response.data)
      commit('SET_ERROR', null)
      return response.data
    } catch (error) {
      commit('SET_ERROR', errorMessage(error))
      return null
    } finally {
      commit('SET_DETAIL_LOADING', false)
    }
  },
  async createPlayerProfile ({ commit, dispatch }, data) {
    commit('SET_OPERATION_LOADING', true)
    try {
      const response = await handicapAPI.createPlayer(data)
      await dispatch('fetchAllHandicaps')
      commit('SET_ERROR', null)
      return response.data
    } catch (error) {
      commit('SET_ERROR', errorMessage(error))
      throw error
    } finally {
      commit('SET_OPERATION_LOADING', false)
    }
  },
  async fetchUserHandicap ({ commit }, userId) {
    try {
      const response = await handicapAPI.listPlayers({ user: userId })
      return responseItems(response)[0] || null
    } catch (error) {
      commit('SET_ERROR', errorMessage(error))
      return null
    }
  },
  async runOperation ({ commit, dispatch }, { operation, playerId, ...payload }) {
    commit('SET_OPERATION_LOADING', true)
    try {
      let response
      if (operation === 'calculate') response = await handicapAPI.calculate(playerId, payload.effectiveOn)
      if (operation === 'publish') response = await handicapAPI.publish(payload.calculationId)
      if (operation === 'validate') response = await handicapAPI.validateRound(payload.roundId)
      if (operation === 'invalidate') response = await handicapAPI.invalidateRound(payload.roundId, payload.reason)
      if (operation === 'pair') response = await handicapAPI.pairRounds(payload.roundId, payload.pairedRoundId)
      if (operation === 'adjust') response = await handicapAPI.adjustRound(payload.roundId, payload.adjustment, payload.reason)
      if (operation === 'updateProfile') response = await handicapAPI.updatePlayer(playerId, payload.data)
      if (operation === 'externalIndex') response = await handicapAPI.recordExternalIndex(playerId, payload.data)
      if (operation === 'createRound') response = await handicapAPI.createRound(payload.data)
      if (!response) throw new Error('Operación de handicap no reconocida.')
      await dispatch('fetchPlayerOverview', playerId)
      await dispatch('fetchAllHandicaps')
      commit('SET_ERROR', null)
      return response.data
    } catch (error) {
      commit('SET_ERROR', errorMessage(error))
      throw error
    } finally {
      commit('SET_OPERATION_LOADING', false)
    }
  },
  clearOverview ({ commit }) { commit('SET_OVERVIEW', null) },
  clearError ({ commit }) { commit('SET_ERROR', null) }
}

export default { namespaced: true, state, getters, mutations, actions }
